/**
 * DataTable Component
 *
 * Универсальный компонент таблицы с единым стилем.
 * Включает:
 * - Подсветку строк при наведении
 * - Курсор pointer для кликабельных строк
 * - Поле поиска (опционально)
 * - Сортировку по колонкам (опционально)
 * - Фильтрацию по колонкам (Excel-style, опционально)
 * - Пустое состояние
 *
 * Использование:
 *
 * <DataTable
 *   columns={[
 *     { key: 'name', label: 'Название', sortable: true },
 *     { key: 'category', label: 'Категория', filterable: true },
 *     { key: 'status', label: 'Статус', filterable: true, filterOptions: [...] },
 *   ]}
 *   data={users}
 *   onRowClick={(row) => navigate(`/users/${row.id}`)}
 *   searchPlaceholder="Поиск..."
 *   filters={filters}
 *   onFiltersChange={setFilters}
 *   renderCell={(row, column) => {
 *     if (column.key === 'name') return <Text fw={500}>{row.name}</Text>;
 *     return row[column.key];
 *   }}
 * />
 */

import React, { useState, useMemo } from 'react';
import {
  Table,
  ScrollArea,
  UnstyledButton,
  Group,
  Text,
  Center,
  TextInput,
  rem,
  Popover,
  ActionIcon,
  Button,
} from '@mantine/core';
import {
  IconSearch,
  IconSelector,
  IconChevronDown,
  IconChevronUp,
  IconFilter,
  IconFilterOff,
} from '@tabler/icons-react';
import ColumnFilterPopover from './ColumnFilterPopover';

// Цвет подсветки при наведении
const HOVER_COLOR = '#F3F3F3';

// Специальное значение для пустых ячеек
const EMPTY_VALUE = '__empty__';

/**
 * Компонент строки таблицы с подсветкой
 */
export const DataTableRow = ({ children, onClick, style, ...props }) => {
  return (
    <Table.Tr
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background-color 150ms ease',
        ...style,
      }}
      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = HOVER_COLOR}
      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
      {...props}
    >
      {children}
    </Table.Tr>
  );
};

/**
 * Компонент заголовка с сортировкой
 */
const SortableHeader = ({ children, sorted, reversed, onSort }) => {
  const Icon = sorted ? (reversed ? IconChevronUp : IconChevronDown) : IconSelector;
  return (
    <UnstyledButton onClick={onSort} style={{ width: '100%' }}>
      <Group justify="space-between" wrap="nowrap">
        <Text fw={500} fz="sm">
          {children}
        </Text>
        <Center>
          <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
        </Center>
      </Group>
    </UnstyledButton>
  );
};

/**
 * Компонент заголовка с фильтром и опциональной сортировкой
 */
const FilterableHeader = ({
  column,
  data,
  sorted,
  reversed,
  onSort,
  filter,
  onFilterChange,
  isSortable,
}) => {
  const [popoverOpened, setPopoverOpened] = useState(false);
  const hasActiveFilter = filter && filter.size > 0;

  return (
    <Group justify="space-between" wrap="nowrap" gap={4}>
      {/* Сортировка или просто текст */}
      {isSortable ? (
        <UnstyledButton onClick={onSort} style={{ flex: 1 }}>
          <Group gap={4} wrap="nowrap">
            <Text fw={500} fz="sm">{column.label}</Text>
            <Center>
              {sorted ? (
                reversed ? (
                  <IconChevronUp style={{ width: rem(14), height: rem(14) }} stroke={1.5} />
                ) : (
                  <IconChevronDown style={{ width: rem(14), height: rem(14) }} stroke={1.5} />
                )
              ) : (
                <IconSelector style={{ width: rem(14), height: rem(14) }} stroke={1.5} />
              )}
            </Center>
          </Group>
        </UnstyledButton>
      ) : (
        <Text fw={500} fz="sm" style={{ flex: 1 }}>{column.label}</Text>
      )}

      {/* Кнопка фильтра */}
      <Popover
        opened={popoverOpened}
        onChange={setPopoverOpened}
        position="bottom-end"
        shadow="md"
        radius="xl"
        withinPortal
      >
        <Popover.Target>
          <ActionIcon
            variant={hasActiveFilter ? 'filled' : 'subtle'}
            color={hasActiveFilter ? 'blue' : 'gray'}
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setPopoverOpened(!popoverOpened);
            }}
          >
            <IconFilter size={14} />
          </ActionIcon>
        </Popover.Target>
        <Popover.Dropdown>
          <ColumnFilterPopover
            column={column}
            data={data}
            filter={filter || new Set()}
            onChange={(newFilter) => {
              onFilterChange(newFilter);
            }}
            onClose={() => setPopoverOpened(false)}
          />
        </Popover.Dropdown>
      </Popover>
    </Group>
  );
};

/**
 * Основной компонент DataTable
 */
const DataTable = ({
  columns,
  data,
  onRowClick,
  searchPlaceholder = 'Поиск по любому полю',
  showSearch = true,
  searchFields,
  renderCell,
  emptyMessage = 'Ничего не найдено',
  minWidth = 700,
  sortable = false,
  defaultSortField = null,
  defaultSortDirection = 'asc',
  // Props для фильтрации
  filters = {},
  onFiltersChange,
  // Props для серверного режима (обратно совместимые)
  skipProcessing = false,
  onSearchChange,
  onSortChange,
  searchValue,
}) => {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState(defaultSortField);
  const [sortReversed, setSortReversed] = useState(defaultSortDirection === 'desc');

  // Проверяем, есть ли активные фильтры
  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some((f) => f && f.size > 0);
  }, [filters]);

  // Обработчик сортировки
  const handleSort = (field) => {
    const newReversed = field === sortBy ? !sortReversed : false;
    if (field === sortBy) {
      setSortReversed(newReversed);
    } else {
      setSortBy(field);
      setSortReversed(false);
    }
    // Серверный режим: делегируем сортировку наверх
    if (onSortChange) {
      onSortChange(field, newReversed ? 'desc' : 'asc');
    }
  };

  // Обработчик изменения фильтра для колонки
  const handleFilterChange = (columnKey, newFilter) => {
    if (onFiltersChange) {
      onFiltersChange({
        ...filters,
        [columnKey]: newFilter,
      });
    }
  };

  // Сброс всех фильтров
  const handleResetFilters = () => {
    if (onFiltersChange) {
      const resetFilters = {};
      columns.forEach((col) => {
        if (col.filterable) {
          resetFilters[col.key] = new Set();
        }
      });
      onFiltersChange(resetFilters);
    }
  };

  // Фильтрация и сортировка данных
  const processedData = useMemo(() => {
    // Серверный режим: данные уже отфильтрованы/отсортированы бэкендом
    if (skipProcessing) {
      return [...data];
    }

    let result = [...data];

    // Применяем фильтры по колонкам
    Object.entries(filters).forEach(([columnKey, selectedValues]) => {
      if (!selectedValues || selectedValues.size === 0) return; // Пустой Set = показать все

      const column = columns.find((c) => c.key === columnKey);
      if (!column) return;

      result = result.filter((row) => {
        const rawValue = row[columnKey];

        // Специальная обработка для "__none__" - показать ничего
        if (selectedValues.has('__none__')) {
          return false;
        }

        // Проверка на пустые значения
        if (rawValue == null || rawValue === '') {
          return selectedValues.has(EMPTY_VALUE);
        }

        // Проверка на конкретные значения
        return selectedValues.has(rawValue);
      });
    });

    // Текстовая фильтрация (поиск)
    if (search.trim()) {
      const query = search.toLowerCase().trim();

      // Собираем колонки для поиска: либо из searchFields, либо все с searchable !== false
      const searchableColumns = searchFields
        ? columns.filter((c) => searchFields.includes(c.key))
        : columns.filter((c) => c.searchable !== false);

      result = result.filter((row) => {
        return searchableColumns.some((column) => {
          const rawValue = row[column.key];

          // Если у колонки есть searchValue - используем её для преобразования
          const searchableValue = column.searchValue
            ? column.searchValue(rawValue, row)
            : rawValue;

          if (searchableValue == null) return false;
          if (Array.isArray(searchableValue)) {
            return searchableValue.some((v) => String(v).toLowerCase().includes(query));
          }
          return String(searchableValue).toLowerCase().includes(query);
        });
      });
    }

    // Сортировка
    if (sortBy) {
      const column = columns.find(c => c.key === sortBy);
      const sortType = column?.sortType || 'string';

      result.sort((a, b) => {
        let aVal = a[sortBy];
        let bVal = b[sortBy];

        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return sortReversed ? -1 : 1;
        if (bVal == null) return sortReversed ? 1 : -1;

        // Числовая сортировка
        if (sortType === 'number') {
          const numA = Number(aVal) || 0;
          const numB = Number(bVal) || 0;
          return sortReversed ? numB - numA : numA - numB;
        }

        // Сортировка по дате
        if (sortType === 'date') {
          const dateA = new Date(aVal).getTime();
          const dateB = new Date(bVal).getTime();
          return sortReversed ? dateB - dateA : dateA - dateB;
        }

        // Строковая сортировка
        const strA = String(aVal).toLowerCase();
        const strB = String(bVal).toLowerCase();
        return sortReversed ? strB.localeCompare(strA, 'ru') : strA.localeCompare(strB, 'ru');
      });
    }

    return result;
  }, [data, filters, search, searchFields, columns, sortBy, sortReversed, skipProcessing]);

  // Рендер ячейки
  const renderCellContent = (row, column) => {
    if (renderCell) {
      const custom = renderCell(row, column);
      if (custom !== undefined) return custom;
    }
    const value = row[column.key];
    if (value == null) return '—';
    return <Text fz="sm">{String(value)}</Text>;
  };

  // Рендер заголовка
  const renderHeader = (column) => {
    const isSortable = sortable && column.sortable !== false;
    const isFilterable = column.filterable === true;

    // Если колонка фильтруемая, используем FilterableHeader
    if (isFilterable && onFiltersChange) {
      return (
        <FilterableHeader
          column={column}
          data={data}
          sorted={sortBy === column.key}
          reversed={sortReversed}
          onSort={() => handleSort(column.key)}
          filter={filters[column.key]}
          onFilterChange={(newFilter) => handleFilterChange(column.key, newFilter)}
          isSortable={isSortable}
        />
      );
    }

    // Обычный заголовок с сортировкой
    if (isSortable) {
      return (
        <SortableHeader
          sorted={sortBy === column.key}
          reversed={sortReversed}
          onSort={() => handleSort(column.key)}
        >
          {column.label}
        </SortableHeader>
      );
    }

    return column.label;
  };

  return (
    <>
      {/* Строка поиска с кнопкой сброса фильтров */}
      {showSearch && (
        <Group mb="md" gap="sm" align="flex-start">
          <TextInput
            placeholder={searchPlaceholder}
            leftSection={<IconSearch style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
            value={searchValue !== undefined ? searchValue : search}
            onChange={(e) => {
              const val = e.target.value;
              if (onSearchChange) {
                onSearchChange(val);
              } else {
                setSearch(val);
              }
            }}
            style={{ flex: 1 }}
            radius="xl"
            size="sm"
          />
          {hasActiveFilters && onFiltersChange && (
            <Button
              variant="light"
              color="blue"
              radius="xl"
              size="md"
              leftSection={<IconFilterOff size={16} />}
              onClick={handleResetFilters}
            >
              Сбросить фильтры
            </Button>
          )}
        </Group>
      )}

      <ScrollArea>
        <Table horizontalSpacing="md" verticalSpacing="xs" miw={minWidth}>
          <Table.Thead>
            <Table.Tr>
              {columns.map((column) => (
                <Table.Th key={column.key} style={column.width ? { width: column.width } : undefined}>
                  {renderHeader(column)}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {processedData.length > 0 ? (
              processedData.map((row, index) => (
                <DataTableRow
                  key={row.id || index}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((column) => (
                    <Table.Td key={column.key}>
                      {renderCellContent(row, column)}
                    </Table.Td>
                  ))}
                </DataTableRow>
              ))
            ) : (
              <Table.Tr>
                <Table.Td colSpan={columns.length}>
                  <Text fw={500} ta="center">
                    {emptyMessage}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </>
  );
};

export default DataTable;
